import React, { useMemo, useState } from 'react';
import { getLeadIntentCopy } from '../content/index.js';
import { calculateEmi, roundDownTo } from '../utils/emi.mjs';
import { formatMoney } from '../utils/format.js';
import { useLeadModal } from './LeadModalContext.js';

const AMOUNT_STEP = 500;
const DEFAULT_AMOUNT = 500000;

function Donut({ principalShare }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const principalLength = circumference * principalShare;
  return (
    <svg
      className="emi-donut"
      viewBox="0 0 140 140"
      role="img"
      aria-label={`Principal is ${Math.round(principalShare * 100)} percent of the total payable amount`}
    >
      <circle cx="70" cy="70" r={radius} fill="none" stroke="#f4b740" strokeWidth="20" />
      <circle
        cx="70"
        cy="70"
        r={radius}
        fill="none"
        stroke="#0c6efd"
        strokeWidth="20"
        strokeDasharray={`${principalLength} ${circumference - principalLength}`}
        strokeDashoffset={circumference / 4}
        transform="rotate(0 70 70)"
      />
    </svg>
  );
}

export default function EmiCalculator({
  totalFee,
  currency = 'INR',
  courseDurationMonths = 24,
  programName,
  leadSource = 'emi_calculator'
}) {
  const { openLeadModal } = useLeadModal();
  const copy = getLeadIntentCopy('emi');

  const maxAmount = useMemo(
    () => Math.max(AMOUNT_STEP, roundDownTo(totalFee && totalFee > 0 ? totalFee : 2000000, AMOUNT_STEP)),
    [totalFee]
  );
  const minAmount = Math.min(10000, maxAmount);
  const initialAmount = totalFee && totalFee > 0 ? maxAmount : Math.min(maxAmount, DEFAULT_AMOUNT);

  const [amount, setAmount] = useState(initialAmount);
  const [rate, setRate] = useState(11);
  const [years, setYears] = useState(5);
  const [useMoratorium, setUseMoratorium] = useState(false);

  const moratoriumMonths = useMoratorium ? courseDurationMonths + 12 : 0;
  const result = useMemo(
    () =>
      calculateEmi({
        principal: amount,
        annualRate: rate,
        years,
        moratoriumMonths
      }),
    [amount, rate, years, moratoriumMonths]
  );

  const principalShare =
    result.totalPayable > 0 ? Math.min(1, result.principal / result.totalPayable) : 1;
  const money = (value) => formatMoney(Math.round(value), currency);

  return (
    <section className="emi-calculator" aria-labelledby="emi-heading">
      <header className="section-header">
        <h2 id="emi-heading">Fee &amp; EMI calculator</h2>
        <p>
          Estimate the monthly instalment if you finance part of the fee with an education loan.
          Figures are indicative only.
        </p>
      </header>

      <div className="emi-layout">
        <div className="emi-controls">
          <label className="emi-field">
            <span className="emi-field-head">
              <span>Loan amount</span>
              <strong>{money(amount)}</strong>
            </span>
            <input
              type="range"
              min={minAmount}
              max={maxAmount}
              step={AMOUNT_STEP}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <span className="emi-range-labels">
              <span>{money(minAmount)}</span>
              <span>{money(maxAmount)}</span>
            </span>
          </label>

          <label className="emi-field">
            <span className="emi-field-head">
              <span>Interest rate (per year)</span>
              <strong>{rate.toFixed(1)}%</strong>
            </span>
            <input
              type="range"
              min="8"
              max="16"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
            <span className="emi-range-labels">
              <span>8%</span>
              <span>16%</span>
            </span>
          </label>

          <label className="emi-field">
            <span className="emi-field-head">
              <span>Repayment period</span>
              <strong>
                {years} year{years === 1 ? '' : 's'}
              </strong>
            </span>
            <input
              type="range"
              min="1"
              max="15"
              step="1"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
            <span className="emi-range-labels">
              <span>1 yr</span>
              <span>15 yrs</span>
            </span>
          </label>

          <label className="emi-check">
            <input
              type="checkbox"
              checked={useMoratorium}
              onChange={(e) => setUseMoratorium(e.target.checked)}
            />
            <span>
              Include moratorium (course duration + 1 year). Simple interest during this period is
              added to the loan.
            </span>
          </label>
        </div>

        <div className="emi-result" aria-live="polite">
          <Donut principalShare={principalShare} />
          <dl className="emi-figures">
            <div className="emi-figure emi-figure-main">
              <dt>Monthly EMI</dt>
              <dd>{money(result.emi)}</dd>
            </div>
            <div className="emi-figure">
              <dt>
                <span className="emi-dot emi-dot-principal" aria-hidden /> Principal
              </dt>
              <dd>{money(result.principal)}</dd>
            </div>
            {result.moratoriumInterest > 0 && (
              <div className="emi-figure">
                <dt>Interest during moratorium</dt>
                <dd>{money(result.moratoriumInterest)}</dd>
              </div>
            )}
            <div className="emi-figure">
              <dt>
                <span className="emi-dot emi-dot-interest" aria-hidden /> Total interest
              </dt>
              <dd>{money(result.totalInterest)}</dd>
            </div>
            <div className="emi-figure">
              <dt>Total payable</dt>
              <dd>{money(result.totalPayable)}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="emi-footer">
        <p className="emi-disclaimer">
          This calculator uses the standard reducing-balance formula. It is not a loan offer and
          BharatShiksha is not a lender. Actual rates, eligibility and moratorium terms depend on
          the lender.
        </p>
        <button
          type="button"
          className="cta-button lead-intent-btn"
          data-lead-intent="emi"
          onClick={() =>
            openLeadModal({
              intent: 'emi',
              leadSource,
              preferredProgram: programName
            })
          }
        >
          {copy.cta || 'Get exact loan options'}
        </button>
      </div>
    </section>
  );
}
